// Staff and roles (spec/07 §6, F-IAM-08/-11, F-ORG-13): the people on the organisation's staff, the
// invitations out to more, and what each role may do — across every venue. The page opens with its
// header and its one primary action (Invite); the work sits in three tabs, and a member or a role
// opens in a centred dialog over the list (2026-10-07). Reading rides `members:read`; each action rides its
// own permission, checked again by the server (spec/13 §1).

import type { Invitation, Member, Role } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, EmptyState, HeroStat, Table, Tabs, useToast, type TableColumn } from '@clavian/ui-web';
import { useMemo, useState, type ReactNode } from 'react';

import { useAccess, useMyVenues } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useScopeIds } from '../../shell/scope';
import { Forbidden, Page, Section, TableSkeleton } from '../../ui';
import { CreateRoleDialog } from './CreateRoleDialog';
import { InviteDialog } from './InviteDialog';
import { MemberDialog } from './MemberDialog';
import { RoleDialog } from './RoleDialog';
import { memberName, useInvitations, useMembers, useRevokeInvitation, useRoles } from './queries';

import '../people.css';
import { useProblem } from '../../api/use-problem';

const MEMBERS_READ = 'members:read';
const MEMBERS_INVITE = 'members:invite';
const MEMBERS_ROLES = 'members:roles';

/** Venue ids to names, for the venue scope a member or an invitation carries. Empty scope is "all". */
export function useVenueNames(): (venueIds: readonly string[]) => string {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const venues = useMyVenues();
  const byId = useMemo(() => {
    const map = new Map<string, string>();
    for (const venue of venues.data ?? []) if (venue.organizationId === scope.organizationId) map.set(venue.id, venue.name);
    return map;
  }, [venues.data, scope.organizationId]);
  return (venueIds) => {
    if (venueIds.length === 0) return t('staff.members.allVenues');
    const names = venueIds.map((id) => byId.get(id) ?? t('staff.members.oneVenue'));
    return names.join(', ');
  };
}

export function StaffPage(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const canRead = access.status !== 'ready' || access.can(MEMBERS_READ);
  const canInvite = access.status === 'ready' && access.can(MEMBERS_INVITE);

  if (access.status === 'ready' && !canRead) {
    return (
      <Page title={t('staff.title')} description={t('staff.intro')}>
        <Forbidden message={t('staff.notPermitted')} />
      </Page>
    );
  }
  return <StaffOverview canInvite={canInvite} />;
}

function StaffOverview({ canInvite }: { readonly canInvite: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const [inviteOpen, setInviteOpen] = useState(false);

  // The header's figures are the members the Members tab reads (the same query), never a count asked for.
  const members = useMembers(scope.organizationId);
  const listed = members.data?.pages.flatMap((page) => page.data);
  const more = members.hasNextPage ? '+' : '';
  const hero =
    listed === undefined
      ? {}
      : {
          stats: (
            <>
              <HeroStat label={t('staff.hero.members')} value={`${listed.length}${more}`} />
              <HeroStat label={t('staff.hero.active')} value={listed.filter((member) => member.status !== 'suspended').length} of={`/ ${listed.length}${more}`} />
              <HeroStat label={t('staff.hero.twoFactor')} value={listed.filter((member) => member.secondFactorEnabled).length} of={`/ ${listed.length}${more}`} />
            </>
          ),
        };
  const onInvite = canInvite ? () => setInviteOpen(true) : undefined;

  return (
    <Page
      title={t('staff.title')}
      description={t('staff.intro')}
      hero={hero}
      actions={
        canInvite ? (
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setInviteOpen(true)}>
            {t('staff.invite.action')}
          </Button>
        ) : undefined
      }
    >
      <StaffTabs onInvite={onInvite} />
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </Page>
  );
}

function StaffTabs({ onInvite }: { readonly onInvite: (() => void) | undefined }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Tabs
      className="console-staff-tabs"
      label={t('staff.tabs')}
      items={[
        { value: 'members', label: t('staff.tab.members'), content: <MembersTab onInvite={onInvite} /> },
        { value: 'invitations', label: t('staff.tab.invitations'), content: <InvitationsTab onInvite={onInvite} /> },
        { value: 'roles', label: t('staff.tab.roles'), content: <RolesTab /> },
      ]}
    />
  );
}

// -------------------------------------------------------------------------------------------------
// Members
// -------------------------------------------------------------------------------------------------

function MembersTab({ onInvite }: { readonly onInvite: (() => void) | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const members = useMembers(scope.organizationId);
  const venueNames = useVenueNames();
  const [selected, setSelected] = useState<Member | null>(null);

  const columns = useMemo<readonly TableColumn<Member>[]>(
    () => [
      {
        key: 'member',
        header: t('staff.members.column.name'),
        cell: (member) => (
          <div className="console-people-cell">
            <Avatar name={memberName(member)} size="sm" decorative />
            <div className="console-people-cell__who">
              <button type="button" className="console-people-cell__name" onClick={() => setSelected(member)}>
                {memberName(member)}
              </button>
              {/* How to reach them under the name — not the phone number again when it is all the name there is. */}
              {member.email !== null ? (
                <span className="console-subtle">{member.email}</span>
              ) : member.phoneNumber !== null && member.phoneNumber !== memberName(member) ? (
                <span className="console-subtle">{member.phoneNumber}</span>
              ) : null}
            </div>
          </div>
        ),
      },
      { key: 'role', header: t('staff.members.column.role'), cell: (member) => <Badge tone="neutral">{member.role}</Badge> },
      { key: 'venues', header: t('staff.members.column.venues'), cell: (member) => <span className="console-subtle">{venueNames(member.venueIds)}</span> },
      {
        key: 'status',
        header: t('staff.members.column.status'),
        cell: (member) =>
          member.status === 'suspended' ? (
            <Badge tone="warn" dot>
              {t('staff.members.suspended')}
            </Badge>
          ) : (
            <Badge tone="ok" dot>
              {t('staff.members.active')}
            </Badge>
          ),
      },
      {
        key: 'twoFactor',
        header: t('staff.members.column.twoFactor'),
        cell: (member) => (member.secondFactorEnabled ? <span>{t('staff.members.twoFactorOn')}</span> : <span className="console-subtle">{t('staff.members.twoFactorOff')}</span>),
      },
    ],
    [t, venueNames],
  );

  if (members.data === undefined) {
    return members.isError ? (
      <LoadFailure error={members.error} message={t('staff.members.unavailable')} onRetry={() => void members.refetch()} />
    ) : (
      <TableSkeleton label={t('staff.members.loading')} />
    );
  }

  const rows = members.data.pages.flatMap((page) => page.data);
  if (rows.length === 0) {
    return <EmptyState icon={<Icon name="user" />} title={t('staff.members.empty.title')} description={t('staff.members.empty.body')} action={onInvite === undefined ? undefined : <Button variant="primary" icon={<Icon name="plus" />} onClick={onInvite}>{t('staff.invite.action')}</Button>} />;
  }

  return (
    <section aria-label={t('staff.members.listLabel')}>
      <div className="console-table-card">
        <Table caption={t('staff.members.caption')} captionHidden columns={columns} rows={rows} rowKey={(member) => member.id} rowTone={(member) => (member.status === 'suspended' ? 'warn' : undefined)} />
      </div>
      {members.hasNextPage ? (
        <div className="console-list-foot">
          <Button variant="ghost" size="sm" busy={members.isFetchingNextPage} onClick={() => void members.fetchNextPage()}>
            {t('staff.members.more')}
          </Button>
        </div>
      ) : null}
      <MemberDialog member={selected} onClose={() => setSelected(null)} />
    </section>
  );
}

// -------------------------------------------------------------------------------------------------
// Invitations
// -------------------------------------------------------------------------------------------------

const INVITATION_TONE: Readonly<Record<Invitation['status'], 'accent' | 'ok' | 'neutral' | 'warn'>> = {
  pending: 'accent',
  accepted: 'ok',
  revoked: 'neutral',
  expired: 'warn',
};

function InvitationsTab({ onInvite }: { readonly onInvite: (() => void) | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const canInvite = access.status === 'ready' && access.can(MEMBERS_INVITE);
  const invitations = useInvitations(scope.organizationId);
  const venueNames = useVenueNames();

  if (invitations.data === undefined) {
    return invitations.isError ? (
      <LoadFailure error={invitations.error} message={t('staff.invitations.unavailable')} onRetry={() => void invitations.refetch()} />
    ) : (
      <TableSkeleton rows={3} label={t('staff.invitations.loading')} />
    );
  }

  if (invitations.data.length === 0) {
    return <EmptyState icon={<Icon name="mail" />} title={t('staff.invitations.empty.title')} description={t('staff.invitations.empty.body')} action={onInvite === undefined ? undefined : <Button variant="primary" icon={<Icon name="plus" />} onClick={onInvite}>{t('staff.invite.action')}</Button>} />;
  }

  return (
    <ul className="console-invitations console-rise-list" aria-label={t('staff.invitations.listLabel')}>
      {invitations.data.map((invitation) => (
        // An invitation's state as the rail at its edge (ADR-0043): out and waiting is live, taken up is settled, the rest over.
        <li
          key={invitation.id}
          className="console-invitation console-rail console-rail--start"
          data-rail={invitation.status === 'pending' ? 'live' : invitation.status === 'accepted' ? 'ok' : 'quiet'}
        >
          <div className="console-invitation__who">
            <span className="console-invitation__dest">{invitation.destination}</span>
            <span className="console-subtle">
              {invitation.role} · {venueNames(invitation.venueIds)}
            </span>
          </div>
          <Badge tone={INVITATION_TONE[invitation.status]} dot>
            {t(`staff.invitations.status.${invitation.status}`)}
          </Badge>
          <span className="console-subtle console-invitation__expiry">
            {invitation.status === 'pending' ? t('staff.invitations.expires', { date: format.date(invitation.expiresAt, 'medium') }) : t('staff.invitations.created', { date: format.date(invitation.createdAt, 'medium') })}
          </span>
          {canInvite && invitation.status === 'pending' ? <InvitationRevoke invitation={invitation} /> : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Revoking a pending invitation ends it for good, so it confirms in place first (BRAND.md §6.1: a
 * confirm for the irreversible) and says so with a toast, rather than firing on a single stray click.
 */
function InvitationRevoke({ invitation }: { readonly invitation: Invitation }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const scope = useScopeIds();
  const revoke = useRevokeInvitation(scope.organizationId);
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
        {t('staff.invitations.revoke')}
      </Button>
    );
  }

  return (
    <span className="console-invitation__confirm" role="group" aria-label={t('staff.invitations.revokeConfirmLabel', { destination: invitation.destination })}>
      <span className="console-subtle">{t('staff.invitations.revokeConfirm')}</span>
      <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
        {t('common.cancel')}
      </Button>
      <Button
        variant="danger"
        size="sm"
        busy={revoke.isPending}
        onClick={() =>
          revoke.mutate(invitation.id, {
            onSuccess: () => show({ tone: 'ok', title: t('staff.invitations.revoked', { destination: invitation.destination }) }),
            onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('staff.invitations.revokeFailed') }) }),
          })
        }
      >
        {t('staff.invitations.revokeYes')}
      </Button>
    </span>
  );
}

// -------------------------------------------------------------------------------------------------
// Roles
// -------------------------------------------------------------------------------------------------

function RolesTab(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const roles = useRoles(scope.organizationId);
  const [selected, setSelected] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);

  const canManage = access.status === 'ready' && access.can(MEMBERS_ROLES);
  // The full catalogue of assignable permissions is the built-in `owner` role's set — the one role that
  // holds them all — so the custom-role editor offers exactly what the server accepts.
  const catalogue = useMemo<readonly string[]>(() => (roles.data ?? []).find((role) => role.name === 'owner')?.permissions ?? [], [roles.data]);

  if (roles.data === undefined) {
    return roles.isError ? (
      <LoadFailure error={roles.error} message={t('staff.roles.unavailable')} onRetry={() => void roles.refetch()} />
    ) : (
      <TableSkeleton rows={4} label={t('staff.roles.loading')} />
    );
  }

  return (
    // The Roles tab names this list: the card keeps what it is for and New role, not a title saying "Roles" again.
    <Section
      body="rows"
      description={t('staff.roles.intro')}
      actions={
        canManage ? (
          <Button variant="primary" size="sm" icon={<Icon name="plus" />} onClick={() => setCreating(true)}>
            {t('staff.roles.new')}
          </Button>
        ) : undefined
      }
    >
      <ul className="console-roles console-rise-list" aria-label={t('staff.roles.listLabel')}>
        {roles.data.map((role) => (
          <li key={role.name}>
            <button type="button" className="console-role" onClick={() => setSelected(role)}>
              <span className="console-role__name">
                {role.name}
                {role.builtIn ? <Badge tone="neutral">{t('staff.roles.builtIn')}</Badge> : <Badge tone="accent">{t('staff.roles.custom')}</Badge>}
              </span>
              <span className="console-subtle">{t('staff.roles.permissionCount', { count: role.permissions.length })}</span>
              <span className="console-role__go" aria-hidden="true">
                <Icon name="chevronRight" />
              </span>
            </button>
          </li>
        ))}
      </ul>
      <RoleDialog role={selected} catalogue={catalogue} onClose={() => setSelected(null)} />
      {canManage ? <CreateRoleDialog organizationId={scope.organizationId} catalogue={catalogue} open={creating} onOpenChange={setCreating} /> : null}
    </Section>
  );
}
